// The page and page-size state of a list page, and its "back to page 1":
//
//   const { page, pageSize, setPage, setPageSize, resetPage } = usePagination();
//   …
//   <DataTable currentPage={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize}
//     toolbar={<DataTableToolbar search={<TableSearch value={q} onChange={(v) => { setQ(v); resetPage(); }} />} />} />
//
// It starts on page 1 with 10 rows, the first of PAGE_SIZES. Reset rules stay the page's own:
// - a page-size change goes back to page 1 unless the page passes `resetOnPageSizeChange: false` (the approval
//   pages' local tables keep their page);
// - a search or filter change resets only where the page calls `resetPage()` (master/user and ceisa-user don't).
// `setPage` and `resetPage` are stable, so effects and `useCallback`s can list them like a `useState` setter.
import { type Dispatch, type SetStateAction, useCallback, useState } from 'react';

export interface UsePaginationOptions {
  /** `setPageSize` also goes back to page 1. Default `true`. */
  resetOnPageSizeChange?: boolean;
}

export interface UsePaginationResult {
  /** The current page, 1-based. */
  page: number;
  pageSize: number;
  /** A `useState` setter: a page or an updater. */
  setPage: Dispatch<SetStateAction<number>>;
  /** Sets the size, then page 1 when `resetOnPageSizeChange` (in that order, like the pages did). */
  setPageSize: (size: number) => void;
  /** Back to page 1. */
  resetPage: () => void;
}

export function usePagination({ resetOnPageSizeChange = true }: UsePaginationOptions = {}): UsePaginationResult {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(10);

  const setPageSize = useCallback(
    (size: number) => {
      setPageSizeState(size);
      if (resetOnPageSizeChange) setPage(1);
    },
    [resetOnPageSizeChange]
  );
  const resetPage = useCallback(() => setPage(1), []);

  return { page, pageSize, setPage, setPageSize, resetPage };
}
